import { gpHome } from '../app';
import { IStorageProvider } from '../../app-common/services/storage-provider-contract';
import {
    AnalysisProjectBackgroundTask,
    AnalysisProjectProcessingServiceName,
    IAnalysisProjectProcessingService,
    ProjectSummary,
    Report,
    ReportSection,
} from './analysis-contract';
import { ImportedProject, ConvertToPlanSummaryResult } from '../../app-plan/scheduling/types';
import { convertToPlanSummary } from '../../app-plan/scheduling/plan-summary-converter';
import {
    CpmIntelligenceServiceName,
    ICpmIntelligenceService,
    type CpmAnalysisCallbacks,
} from '../../app-plan/scheduling/cpm-intelligence-service';
import { buildClientReportModel } from '../../app-plan/scheduling/client-report/report-model';
import { renderClientReportDocument } from '../../app-plan/scheduling/client-report/client-report-renderer';
import {
    buildDisplayNameMaps,
    resolveReportEntityIds,
    resolveEntityIds,
} from '../../app-plan/scheduling/client-report/entity-resolver';
import type { RawRiskReport, ReportState } from '../../app-plan/scheduling/client-report/types';

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

interface ProcessingState {
    projectId: string;
    projectContext: string;
    storageProvider: IStorageProvider;
    tasks: AnalysisProjectBackgroundTask[];
    summary: ProjectSummary | null;
    reports: Report[];
    importedProject: ImportedProject | null;
    convertedData: ConvertToPlanSummaryResult | null;
    analysisText: string;
    cancelAnalysis: (() => void) | null;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class AnalysisProjectProcessingService implements IAnalysisProjectProcessingService {

    static $inject = ['$rootScope', '$timeout', CpmIntelligenceServiceName];
    static serviceName = AnalysisProjectProcessingServiceName;

    readonly states = new Map<string, ProcessingState>();

    constructor(
        private readonly $rootScope: ng.IRootScopeService,
        private readonly $timeout: ng.ITimeoutService,
        private readonly cpmIntelligence: ICpmIntelligenceService,
    ) { }

    /**
     * Initialise in-memory state from an already-imported project.
     * Called after the import endpoint returns the converted data.
     * Sets up Task 1 as complete and Task 2 as ready to run.
     */
    initFromImport(projectId: string, importedProjectData: any, storageProvider: IStorageProvider): void {
        const now = new Date().toISOString();

        const importedProject: ImportedProject = importedProjectData;
        const converted = convertToPlanSummary(importedProject);

        const state: ProcessingState = {
            projectId,
            projectContext: '',
            storageProvider,
            tasks: [
                {
                    id: this.makeId('task'),
                    analysisProjectId: projectId,
                    name: 'Initial Processing',
                    status: 'complete',
                    progressPercent: 100,
                    progressMessage: null,
                    startedUtc: now,
                    completedUtc: now,
                },
                {
                    id: this.makeId('task'),
                    analysisProjectId: projectId,
                    name: 'Generating Risk Report',
                    status: 'pending',
                    progressPercent: 0,
                    progressMessage: null,
                    startedUtc: null,
                    completedUtc: null,
                },
            ],
            summary: this.deriveSummary(converted),
            reports: [{
                id: projectId + '-risk',
                title: 'Risk Report',
                sections: [],
                isComplete: false,
                generatedUtc: null,
            }],
            importedProject,
            convertedData: converted,
            analysisText: '',
            cancelAnalysis: null,
        };

        this.states.set(projectId, state);
    }

    restoreSavedReports(projectId: string, reports: Report[]): void {
        const state = this.states.get(projectId);
        if (!state || reports.length === 0) return;

        state.reports = reports;
        const task2 = state.tasks[1];
        if (task2) {
            task2.status = 'complete';
            task2.progressPercent = 100;
            task2.completedUtc = reports[0].generatedUtc || new Date().toISOString();
        }
    }

    getTaskStatus(projectId: string): AnalysisProjectBackgroundTask[] {
        const state = this.states.get(projectId);
        return state ? state.tasks : [];
    }

    getProjectSummary(projectId: string): ProjectSummary | null {
        const state = this.states.get(projectId);
        return state ? state.summary : null;
    }

    getReport(projectId: string, reportId: string): Report | null {
        const state = this.states.get(projectId);
        if (!state) return null;
        return state.reports.find(r => r.id === reportId) || null;
    }

    /**
     * Set the project context before Task 2 runs. Called from the detail
     * component when the user edits the project context textarea.
     */
    setProjectContext(projectId: string, context: string): void {
        const state = this.states.get(projectId);
        if (state) {
            state.projectContext = context;
        }
    }

    /**
     * Trigger Task 2 (risk analysis) manually. Called when the user clicks
     * "Generate Report" after providing project context.
     */
    startRiskAnalysis(projectId: string): void {
        const state = this.states.get(projectId);
        if (!state || !state.convertedData) return;
        if (state.tasks[1].status === 'running') return;
        this.runRiskAnalysisTask(state);
    }

    // -----------------------------------------------------------------------
    // Task 2: Risk analysis via AI
    // -----------------------------------------------------------------------

    private runRiskAnalysisTask(state: ProcessingState): void {
        const task = state.tasks[1];
        const report = state.reports[0];

        task.status = 'running';
        task.startedUtc = new Date().toISOString();
        task.progressPercent = 5;
        task.progressMessage = 'Preparing analysis input...';
        state.analysisText = '';
        report.sections = [];
        report.isComplete = false;
        this.$rootScope.$applyAsync();

        // Note: callbacks are invoked inside $timeout by CpmIntelligenceService,
        // so Angular digest is already triggered — no need for $applyAsync here.
        let thinkingCount = 0;
        let analysisCount = 0;
        const callbacks: CpmAnalysisCallbacks = {
            onThinking: (text: string) => {
                thinkingCount++;
                console.log(`[analyze] onThinking #${thinkingCount}: "${(text || '').substring(0, 80)}..."`);
                task.progressMessage = (text || '').substring(0, 200);
                task.progressPercent = Math.min(30, task.progressPercent + 2);
            },
            onAnalysis: (text: string) => {
                analysisCount++;
                console.log(`[analyze] onAnalysis #${analysisCount}: ${(text || '').length} chars, total: ${state.analysisText.length + (text || '').length}`);
                state.analysisText += text;
                task.progressMessage = 'Writing analysis...';
                task.progressPercent = Math.min(80, 30 + Math.floor(state.analysisText.length / 80));
                report.sections = this.buildProgressiveSections(state.analysisText);
            },
            onComplete: (rawReport: RawRiskReport, analysisText: string) => {
                console.log(`[analyze] onComplete — ${rawReport?.risks?.length ?? 0} risks, ${thinkingCount} thinking events, ${analysisCount} analysis events`);
                task.progressPercent = 90;
                task.progressMessage = 'Generating final report...';
                this.$timeout(() => {
                    this.finishReport(state, rawReport, analysisText);
                }, 100);
            },
            onError: (message: string) => {
                console.error(`[analyze] onError — ${message}`);
                task.status = 'error';
                task.progressMessage = 'Analysis failed: ' + message;
            },
        };

        const cancellable = this.cpmIntelligence.analyzeRisk({
            convertedData: state.convertedData!,
            projectContext: state.projectContext,
            planId: state.projectId,
        }, callbacks);

        state.cancelAnalysis = cancellable.cancel;
    }

    private finishReport(state: ProcessingState, rawReport: RawRiskReport, analysisText: string): void {
        const task = state.tasks[1];
        const report = state.reports[0];

        // Resolve entity IDs
        const idMaps = buildDisplayNameMaps(state.convertedData!);
        const resolvedReport = resolveReportEntityIds(rawReport as any, idMaps) as unknown as RawRiskReport;
        const resolvedAnalysisText = resolveEntityIds(analysisText, idMaps);

        // Build branded report
        const reportState: ReportState = {
            report: resolvedReport,
            savedAt: new Date().toISOString(),
            analysisText: resolvedAnalysisText,
        };

        const clientReportModel = buildClientReportModel(state.importedProject!, reportState);

        if (clientReportModel) {
            const reportHtml = renderClientReportDocument(clientReportModel);
            report.sections = [{
                heading: 'Risk Report',
                bodyHtml: reportHtml,
            }];
        } else {
            // Fallback: show raw analysis text
            report.sections = this.buildProgressiveSections(resolvedAnalysisText);
        }

        report.isComplete = true;
        report.generatedUtc = new Date().toISOString();
        task.progressPercent = 100;
        task.status = 'complete';
        task.progressMessage = null;
        task.completedUtc = new Date().toISOString();
        this.$rootScope.$applyAsync();

        // Persist report to blob storage via the storage provider
        this.saveReport(state, report, rawReport, analysisText, resolvedReport, resolvedAnalysisText, reportState);
    }

    private saveReport(
        state: ProcessingState,
        report: Report,
        rawReport: RawRiskReport,
        rawAnalysisText: string,
        resolvedReport: RawRiskReport,
        resolvedAnalysisText: string,
        reportState: ReportState,
    ): void {
        const sp = state.storageProvider;
        const basePath = 'reports/' + report.id;
        const write = (path: string, data: any, ct: string) =>
            sp.write(path, new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], { type: ct }), ct);

        // Report metadata
        write(basePath + '.json', {
            id: report.id,
            title: report.title,
            isComplete: report.isComplete,
            generatedUtc: report.generatedUtc,
            sectionCount: report.sections.length,
            projectContext: state.projectContext,
        }, 'application/json')
            .then(() => console.log('[processing] saved report metadata', report.id));

        // Rendered HTML
        if (report.sections.length > 0) {
            const html = report.sections.map(s => {
                const parts: string[] = [];
                if (s.heading) parts.push('<h2>' + s.heading + '</h2>');
                if (s.bodyHtml) parts.push(s.bodyHtml);
                return parts.join('\n');
            }).join('\n');
            write(basePath + '.html', html, 'text/html')
                .then(() => console.log('[processing] saved report HTML', report.id));
        }

        // Raw AI report (before entity ID resolution)
        write(basePath + '-raw-report.json', rawReport, 'application/json')
            .then(() => console.log('[processing] saved raw report', report.id));

        // Resolved AI report (after entity ID resolution)
        write(basePath + '-resolved-report.json', resolvedReport, 'application/json')
            .then(() => console.log('[processing] saved resolved report', report.id));

        // Raw analysis text (AI prose before entity resolution)
        write(basePath + '-raw-analysis.md', rawAnalysisText, 'text/markdown')
            .then(() => console.log('[processing] saved raw analysis text', report.id));

        // Resolved analysis text (after entity resolution)
        write(basePath + '-resolved-analysis.md', resolvedAnalysisText, 'text/markdown')
            .then(() => console.log('[processing] saved resolved analysis text', report.id));

        // Full report state (the input to buildClientReportModel)
        write(basePath + '-report-state.json', reportState, 'application/json')
            .then(() => console.log('[processing] saved report state', report.id));
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    private deriveSummary(converted: ConvertToPlanSummaryResult): ProjectSummary {
        const ps = converted.planSummary;
        let totalDurationDays = 0;
        if (ps.plannedStartDate && ps.plannedEndDate) {
            const start = new Date(ps.plannedStartDate);
            const end = new Date(ps.plannedEndDate);
            totalDurationDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        }
        let criticalPathLength = 0;
        if (ps.criticalPaths && ps.criticalPaths.length > 0) {
            criticalPathLength = ps.criticalPaths.reduce(
                (max: number, path) => Math.max(max, path.ids ? path.ids.length : 0), 0,
            );
        }
        return {
            projectName: ps.planName,
            activityCount: ps.metrics.taskCount,
            resourceCount: ps.metrics.resourceCount,
            startDate: ps.plannedStartDate || '',
            endDate: ps.plannedEndDate || '',
            totalDurationDays,
            criticalPathLength,
            milestoneCount: ps.metrics.milestoneCount,
        };
    }

    private buildProgressiveSections(text: string): ReportSection[] {
        if (!text) return [];
        const sections: ReportSection[] = [];
        const parts = text.split(/(?=^## )/m);
        for (const part of parts) {
            const headingMatch = part.match(/^## (.+)\n/);
            if (headingMatch) {
                sections.push({
                    heading: headingMatch[1].trim(),
                    bodyHtml: this.simpleMarkdownToHtml(part.replace(/^## .+\n/, '')),
                });
            } else if (part.trim()) {
                sections.push({
                    heading: 'Analysis',
                    bodyHtml: this.simpleMarkdownToHtml(part),
                });
            }
        }
        return sections;
    }

    private simpleMarkdownToHtml(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/^/, '<p>')
            .replace(/$/, '</p>');
    }

    private makeId(prefix: string): string {
        return `${prefix}-${Math.random().toString(36).substring(2, 8)}`;
    }
}

gpHome.service(AnalysisProjectProcessingService.serviceName, AnalysisProjectProcessingService);
